/**
 * ObsCriterionMappingPanel — main content for one Criterion Mapping page.
 *
 * Layout (criteria-by-criteria redesign):
 *   • Sticky criterion title (SSI pattern)
 *   • For DSM criteria (A1–B4): a single evidence-decision button row
 *   • Two side-by-side note panels (ADOS / Task Administration) showing notes
 *     tagged to this page. Cards stay taggable here so clinicians can re-tag
 *     without leaving the phase — writes go back to the same Task Admin /
 *     ADOS arrays via `onUpdateNoteTags`. Untagging *this* page asks for
 *     confirmation, then animates the note out.
 *   • A third notes box underneath:
 *       – A1–B4 / Other Behaviours: optional free-text bullets
 *         (`criteriaNotes.{id}`), labelled without "criterion" wording on
 *         the non-DSM pages.
 *       – Self-Reported Behaviours: re-presents notes typed directly into
 *         the Task Administration Self-Report box (`general_self_reported`),
 *         editable + taggable so that box stays the single source of truth.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { ExclamationIcon } from '@heroicons/react/solid';
import StructuredNotesEditor from './StructuredNotesEditor';
import ObsTaggedNotesPanel from './ObsTaggedNotesPanel';
import { DSM5_EVIDENCE_OPTIONS } from '../config/observationTasksConfig';
import { OBS_NOTE_TAG_LABEL_BY_ID } from '../config/obsNoteTagOptions';
import { AUTISM_EVIDENCE_BUTTON_COLOURS } from '../../../../constants/autismEvidenceColors';
import { SELF_REPORTED_TAG_ID } from '../../../../utils/obsSelfReportGeneration';

/** How long the untag exit animation runs before we drop the note from the UI. */
const UNTAG_EXIT_MS = 300;

export default function ObsCriterionMappingPanel({
  page,
  evidence = null,
  onEvidenceChange,
  taskNotes = [],
  adosNotes = [],
  /**
   * Optional free-text notes for this criterion / Other Behaviours page
   * (structured notes array). Stored separately from tagged Task Admin /
   * ADOS notes. Not used on the Self-Reported page (see selfReportNotes).
   */
  criterionNotes = [],
  onCriterionNotesChange,
  /**
   * Self-Reported page only: notes from the Task Administration Self-Report
   * box (`general_self_reported.notes`). Shown in the third notes area so
   * clinicians see (and can edit / re-tag) everything typed there directly.
   */
  selfReportNotes = null,
  onSelfReportNotesChange = null,
  /**
   * Persist a re-tag on a Task Admin / ADOS note.
   * (sourceRef, noteId, criteriaTags) => void
   */
  onUpdateNoteTags,
  readOnly = false,
}) {
  const [pendingUntag, setPendingUntag] = useState(null);
  // Snapshots keep an untagged note mounted while it finishes collapsing out
  // of the list (the live tagged collection drops it as soon as the write lands).
  const [exitingSnapshots, setExitingSnapshots] = useState([]);
  // Separate from snapshots so we can paint one frame at full height before
  // applying the exit class — otherwise CSS has no "from" state to transition.
  const [exitingNoteIds, setExitingNoteIds] = useState(() => new Set());

  // Clear any in-flight exit animation when navigating to another criterion —
  // otherwise a stale snapshot could flash on the next page.
  useEffect(() => {
    setExitingSnapshots([]);
    setExitingNoteIds(new Set());
    setPendingUntag(null);
  }, [page?.id]);

  const handleRequestRemoveProtectedTag = useCallback((payload) => {
    setPendingUntag(payload);
  }, []);

  const confirmUntag = useCallback(() => {
    if (!pendingUntag || !onUpdateNoteTags) {
      setPendingUntag(null);
      return;
    }
    const { note, nextTags } = pendingUntag;
    setPendingUntag(null);

    // 1) Mount a snapshot (still at full height, not yet "exiting").
    setExitingSnapshots((prev) => (
      prev.some((n) => n.id === note.id) ? prev : [...prev, note]
    ));

    // 2) Next frame: kick the collapse animation and persist the untag so the
    //    live list drops the note — the snapshot keeps it on screen until done.
    requestAnimationFrame(() => {
      setExitingNoteIds((prev) => {
        const next = new Set(prev);
        next.add(note.id);
        return next;
      });
      onUpdateNoteTags(note.sourceRef, note.id, nextTags);
    });

    window.setTimeout(() => {
      setExitingSnapshots((prev) => prev.filter((n) => n.id !== note.id));
      setExitingNoteIds((prev) => {
        const next = new Set(prev);
        next.delete(note.id);
        return next;
      });
    }, UNTAG_EXIT_MS);
  }, [pendingUntag, onUpdateNoteTags]);

  if (!page) return null;

  const hasEvidenceDecision = page.hasEvidenceDecision !== false;
  const isSelfReportedPage = page.id === SELF_REPORTED_TAG_ID;
  const isOtherBehavioursPage = page.id === 'other';
  const canEdit = !readOnly;
  const protectedLabel = OBS_NOTE_TAG_LABEL_BY_ID[page.id] || page.fullTitle || page.id;

  // Third-box copy — DSM pages keep "criterion"; Self-Reported / Other are
  // distinct narrative buckets, so we avoid implying they are DSM criteria.
  let thirdBoxTitle = 'Additional criterion notes';
  let thirdBoxSubtitle = 'Optional — capture anything for this criterion that is not already covered by the tagged notes above.';
  let thirdBoxPlaceholder = 'Type an additional note and press Enter…';
  let thirdBoxEmpty = 'No additional notes recorded';
  if (isSelfReportedPage) {
    thirdBoxTitle = 'Self-Report notes';
    thirdBoxSubtitle = 'Notes typed directly in the Task Administration Self-Report box — edit or re-tag here if needed.';
    thirdBoxPlaceholder = 'Type a self-reported observation and press Enter…';
    thirdBoxEmpty = 'No Self-Report notes recorded yet';
  } else if (isOtherBehavioursPage) {
    thirdBoxTitle = 'Additional notes';
    thirdBoxSubtitle = 'Optional — capture anything for Other Behaviours that is not already covered by the tagged notes above.';
    thirdBoxPlaceholder = 'Type an additional note and press Enter…';
    thirdBoxEmpty = 'No additional notes recorded';
  }

  const thirdBoxNotes = isSelfReportedPage
    ? (Array.isArray(selfReportNotes) ? selfReportNotes : [])
    : criterionNotes;
  const onThirdBoxNotesChange = isSelfReportedPage
    ? onSelfReportNotesChange
    : onCriterionNotesChange;

  return (
    <div className="flex-grow w-full max-w-6xl mx-auto px-8 py-6 text-left">
      {/* Sticky title — same affordance as Adult SSI criterion pages. */}
      <div className="sticky top-0 z-10 -mx-8 px-8 py-3 mb-4 bg-white/95 backdrop-blur border-b border-gray-100">
        <h1 className="text-gray-900 text-2xl font-bold leading-tight">
          {page.fullTitle || page.shortTitle}
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          {hasEvidenceDecision
            ? 'Review tagged notes from ADOS-informed Coding and Task Administration, then record a single evidence decision for this criterion. You can re-tag notes here if needed.'
            : isSelfReportedPage
              ? 'Review notes tagged Self-Reported Behaviours from ADOS-informed Coding and Task Administration, plus notes typed directly in the Self-Report box. You can re-tag notes here if needed.'
              : 'Review tagged notes that will feed into the narrative for this section. You can re-tag notes here if needed.'}
        </p>
      </div>

      {hasEvidenceDecision && (
        <div className="mb-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
            Evidence decision
          </p>
          <div className="inline-flex flex-wrap gap-2">
            {DSM5_EVIDENCE_OPTIONS.map((opt) => {
              const colours = AUTISM_EVIDENCE_BUTTON_COLOURS[opt] || {};
              const isSelected = evidence === opt;
              return (
                <button
                  key={opt}
                  type="button"
                  disabled={readOnly}
                  onClick={() => {
                    if (readOnly || !onEvidenceChange) return;
                    // Toggle off if the clinician clicks the already-selected
                    // option — same pattern as the old per-task buttons.
                    onEvidenceChange(isSelected ? null : opt);
                  }}
                  className={`
                    px-3.5 py-2 rounded-lg text-xs font-semibold border
                    transition-all duration-150
                    ${readOnly ? 'cursor-default' : ''}
                    ${isSelected
                      ? `${colours.selectedBg} ${colours.selectedBorder} text-white shadow-sm`
                      : `bg-white text-gray-600 border-gray-300 ${readOnly ? '' : colours.hoverBorder || ''}`}
                  `}
                  aria-pressed={isSelected}
                  aria-label={`${opt} for ${page.id}`}
                >
                  {opt}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
        <ObsTaggedNotesPanel
          title="ADOS-informed Coding"
          subtitle="Notes tagged while scoring ADOS items — Tag to reassign"
          notes={adosNotes}
          emptyMessage="No ADOS notes tagged to this section yet."
          pageId={page.id}
          canEdit={canEdit}
          onUpdateNoteTags={onUpdateNoteTags}
          exitingNoteIds={exitingNoteIds}
          exitingSnapshots={exitingSnapshots.filter((n) => n.sourceKind === 'ados')}
          onRequestRemoveProtectedTag={handleRequestRemoveProtectedTag}
        />
        <ObsTaggedNotesPanel
          title="Task Administration"
          subtitle="Notes tagged during observation tasks — Tag to reassign"
          notes={taskNotes}
          emptyMessage="No task notes tagged to this section yet."
          pageId={page.id}
          canEdit={canEdit}
          onUpdateNoteTags={onUpdateNoteTags}
          exitingNoteIds={exitingNoteIds}
          exitingSnapshots={exitingSnapshots.filter((n) => n.sourceKind === 'task')}
          onRequestRemoveProtectedTag={handleRequestRemoveProtectedTag}
        />
      </div>

      {/* Third notes area — DSM/Other use criteriaNotes; Self-Reported
          re-presents the Task Admin Self-Report box so those notes are not
          duplicated in the Task Administration tagged panel above. */}
      <div className="mt-5 bg-white rounded-xl border border-gray-200 shadow-sm overflow-visible">
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/80 rounded-t-xl">
          <h3 className="text-sm font-bold text-gray-900">{thirdBoxTitle}</h3>
          <p className="text-xs text-gray-500 mt-0.5">{thirdBoxSubtitle}</p>
        </div>
        <div className="p-3 overflow-visible">
          <StructuredNotesEditor
            notes={thirdBoxNotes}
            onChange={readOnly ? undefined : onThirdBoxNotesChange}
            placeholder={thirdBoxPlaceholder}
            readOnlyPlaceholder={thirdBoxEmpty}
            readOnly={readOnly}
            minHeight="7.5rem"
            domainId={`criterion-notes-${page.id}`}
            // Self-Reported third box is the live Self-Report notes array —
            // keep Tag + auto-tag SR so it stays in sync with Task Admin.
            // Other pages' free-text bullets are untagged extras.
            enableCriteriaTagging={isSelfReportedPage && canEdit}
            defaultCriteriaTags={isSelfReportedPage ? [SELF_REPORTED_TAG_ID] : null}
          />
        </div>
      </div>

      {/* Confirm before untagging the current page — the note will leave
          this page (it remains available elsewhere if other tags remain). */}
      {pendingUntag && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setPendingUntag(null)}
          />
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="untag-confirm-title"
            className="relative bg-white rounded-xl shadow-2xl p-6 w-[480px] max-w-[90vw] text-left"
          >
            <button
              type="button"
              onClick={() => setPendingUntag(null)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 text-xl leading-none"
              aria-label="Close"
            >
              &times;
            </button>
            <div className="flex items-center gap-2 mb-3">
              <ExclamationIcon className="w-6 h-6 text-amber-500 flex-shrink-0" />
              <h2 id="untag-confirm-title" className="text-lg font-bold text-gray-900">
                {hasEvidenceDecision
                  ? 'Remove tag from this criterion?'
                  : 'Remove tag from this section?'}
              </h2>
            </div>
            <p className="text-sm text-gray-600 mb-2">
              This note will no longer appear under{' '}
              <span className="font-semibold text-gray-800">{protectedLabel}</span>.
              {Array.isArray(pendingUntag.nextTags) && pendingUntag.nextTags.length > 0
                ? ' It will remain tagged to its other sections.'
                : ' It will have no criteria tags until you tag it again.'}
            </p>
            {/* Grow with the note up to ~12 lines, then scroll — never hard-clip
                mid-sentence the way line-clamp did on moderately long notes. */}
            <div className="text-sm text-gray-500 italic bg-gray-50 border border-gray-100 rounded-lg p-3 mb-5 max-h-48 overflow-y-auto">
              <p className="whitespace-pre-wrap break-words">
                “{pendingUntag.note.text}”
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setPendingUntag(null)}
                className="py-2 px-4 rounded-md shadow-sm text-gray-900 border border-gray-300 hover:bg-gray-50 transition-all duration-200 font-semibold text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmUntag}
                className="py-2 px-4 rounded-md shadow-md bg-primary text-white hover:bg-primary/90 transition-all duration-200 font-semibold text-sm"
              >
                Remove tag
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
